import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, ArrowRight, Landmark } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INDIAN_STATES } from "@/lib/india";
import FloLogo from "@/components/FloLogo";
import RbiBadge from "@/components/RbiBadge";
import { ONBOARDING } from "@/constants/testIds";

const BUSINESS_TYPES = [
  { value: "sole_proprietor", label: "Sole Proprietor" },
  { value: "partnership", label: "Partnership" },
  { value: "company", label: "Private Limited / OPC" },
  { value: "limited_liability_partnership", label: "Limited Liability Partnership (LLP)" },
];

const PRODUCTS = ["Spices", "Tea", "Coffee", "Other"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [form, setForm] = useState({
    legal_name: "",
    owner_name: "",
    phone: "+91 ",
    email: user?.email || "",
    address_line: "",
    city: "",
    state: "",
    postal_code: "",
    country: "IN",
    primary_product: "",
    business_type: "",
    tax_gst: "",
    business_id: "",
    tax_id: "",
    // Payout bank account (INR)
    payout_number: "",
    payout_ifsc: "",
    payout_holder: "",
    payout_same_as_business: true,
    payout_address_line: "",
    payout_city: "",
    payout_state: "",
    payout_postal_code: "",
  });
  const [loading, setLoading] = useState(false);

  // Keep the form email in sync once the auth user finishes loading.
  useEffect(() => {
    if (user?.email && !form.email) {
      setForm((f) => ({ ...f, email: user.email }));
    }
  }, [user?.email, form.email]);

  const set = (k) => (e) => {
    const v = typeof e === "string" ? e : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();

    // Per-field validation — tell the user exactly what's missing.
    const missing = [];

    // Business
    if (!form.legal_name.trim()) missing.push("Business / legal name");
    if (!form.owner_name.trim()) missing.push("Owner full name");
    if (!form.phone.trim() || form.phone.trim() === "+91") missing.push("Phone");
    if (!form.email.trim()) missing.push("Business email");
    if (!form.business_type) missing.push("Business type");
    if (!form.primary_product) missing.push("Primary export product");

    // Business address — now all required
    if (!form.address_line.trim()) missing.push("Address line");
    if (!form.city.trim()) missing.push("City");
    if (!form.state.trim()) missing.push("State");
    if (!form.postal_code.trim()) missing.push("Postal code");

    // Verification / KYB — required text fields per business type
    if (form.business_type === "sole_proprietor" || form.business_type === "partnership") {
      if (!form.tax_gst.trim()) missing.push("GSTIN");
    }
    if (form.business_type === "company" || form.business_type === "limited_liability_partnership") {
      if (!form.business_id.trim()) missing.push(form.business_type === "company" ? "CIN" : "LLP ID");
      if (!form.tax_id.trim()) missing.push("Business PAN");
      if (!form.tax_gst.trim()) missing.push("GSTIN");
    }

    // Payout
    if (!form.payout_number.trim()) missing.push("Bank account number");
    if (!form.payout_ifsc.trim()) missing.push("IFSC code");
    if (!form.payout_holder.trim()) missing.push("Account holder name");
    if (!form.payout_same_as_business) {
      if (!form.payout_address_line.trim()) missing.push("Payout address line");
      if (!form.payout_city.trim()) missing.push("Payout city");
      if (!form.payout_state.trim()) missing.push("Payout state");
      if (!form.payout_postal_code.trim()) missing.push("Payout postal code");
    }

    // Demo walkthrough: skip the required-fields check too. Backend accepts
    // anything for these accounts, so the client shouldn't block them.
    if (missing.length && !user?.is_demo) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      return;
    }

    // Demo walkthrough accounts skip every format validation below —
    // matches backend `is_demo` bypass so a demo can be replayed with
    // nonsense placeholder values.
    const isDemo = !!user?.is_demo;

    // Client-side IFSC format check (server also validates)
    const ifsc = form.payout_ifsc.trim().toUpperCase();
    if (!isDemo && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      toast.error("Invalid IFSC. Format: 4 letters + 0 + 6 alphanumeric (e.g. HDFC0000261).");
      return;
    }

    // Postal code — must be exactly 6 digits
    if (!isDemo && form.postal_code && !/^\d{6}$/.test(form.postal_code.trim())) {
      toast.error("Please enter a valid 6-digit PIN code");
      return;
    }

    // GSTIN — 15 char alphanumeric where required
    if (
      !isDemo &&
      (form.business_type === "sole_proprietor" ||
        form.business_type === "partnership" ||
        form.business_type === "company" ||
        form.business_type === "limited_liability_partnership") &&
      form.tax_gst &&
      !/^[A-Z0-9]{15}$/.test(form.tax_gst.trim().toUpperCase())
    ) {
      toast.error("GSTIN must be 15 alphanumeric characters (e.g. 32ABCDE1234F1Z5)");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        legal_name: form.legal_name,
        owner_name: form.owner_name,
        phone: form.phone,
        email: form.email,
        physical_address: {
          address_line: form.address_line,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
        },
        primary_product: form.primary_product,
        business_type: form.business_type,
        tax_gst: form.tax_gst || null,
        business_id: form.business_id || null,
        tax_id: form.tax_id || null,
        payout: {
          number: form.payout_number.trim(),
          ifsc,
          holder_name: form.payout_holder.trim(),
          billing_same_as_business: !!form.payout_same_as_business,
          billing_address: form.payout_same_as_business
            ? null
            : {
                address_line: form.payout_address_line,
                city: form.payout_city,
                state: form.payout_state,
                postal_code: form.payout_postal_code,
                country: "IN",
              },
        },
      };
      await api.post("/onboarding", payload);
      await refresh();
      toast.success("Onboarding complete");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const bt = form.business_type;

  return (
    <div className="min-h-screen">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto flex items-center gap-3 p-4 md:p-6">
          <a
            href="https://useflopayments.com"
            className="flex items-center gap-3 group"
            data-testid="onboarding-logo-home-link"
            aria-label="Flo Payments — exit onboarding and return to marketing site"
          >
            <FloLogo variant="lockup-light" size={36} className="group-hover:opacity-80 transition-opacity" />
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 md:p-10">
        <RbiBadge className="mb-8" />
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight mb-3">
          Set up your account
        </h1>
        <p className="text-muted-foreground text-base mb-8 max-w-xl">
          {"A few details so we can set up your USD receiving account. (GBP and EUR available on request after onboarding.)"}
        </p>

        <form onSubmit={submit} className="space-y-8">
          {/* Business */}
          <Card>
            <CardContent className="p-6 md:p-8 space-y-6">
              <h2 className="font-display text-xl font-bold">Business</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Business / legal name" required>
                  <Input value={form.legal_name} onChange={set("legal_name")} className="h-12" data-testid={ONBOARDING.legalName} />
                </Field>
                <Field label="Owner full name" required>
                  <Input value={form.owner_name} onChange={set("owner_name")} className="h-12" data-testid={ONBOARDING.ownerName} />
                </Field>
                <Field label="Phone (for records & support)" required>
                  <Input value={form.phone} onChange={set("phone")} placeholder="+91 98470 12345" className="h-12" data-testid={ONBOARDING.phone} />
                </Field>
                <Field label="Business email" required>
                  <Input type="email" value={form.email} onChange={set("email")} className="h-12" data-testid={ONBOARDING.email} />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Business type" required>
                  <Select value={form.business_type || undefined} onValueChange={set("business_type")}>
                    <SelectTrigger className="h-12" data-testid={ONBOARDING.businessType}>
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((b) => (
                        <SelectItem key={b.value} value={b.value}>
                          {b.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Primary export product" required>
                  <Select value={form.primary_product || undefined} onValueChange={set("primary_product")}>
                    <SelectTrigger className="h-12" data-testid={ONBOARDING.primaryProduct}>
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCTS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardContent className="p-6 md:p-8 space-y-6">
              <h2 className="font-display text-xl font-bold">Business address</h2>
              <Field label="Address line" required>
                <Input value={form.address_line} onChange={set("address_line")} className="h-12" data-testid={ONBOARDING.addressLine} />
              </Field>
              <div className="grid md:grid-cols-3 gap-6">
                <Field label="City" required>
                  <Input value={form.city} onChange={set("city")} className="h-12" data-testid={ONBOARDING.city} />
                </Field>
                <Field label="State" required>
                  <Select value={form.state || undefined} onValueChange={set("state")}>
                    <SelectTrigger className="h-12" data-testid={ONBOARDING.state}>
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {INDIAN_STATES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Postal code" required>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={6}
                    value={form.postal_code}
                    onChange={(e) => {
                      const only = e.target.value.replace(/\D+/g, "").slice(0, 6);
                      setForm((f) => ({ ...f, postal_code: only }));
                    }}
                    placeholder="6-digit PIN"
                    className="h-12"
                    data-testid={ONBOARDING.postal}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* Conditional block */}
          <Card>
            <CardContent className="p-6 md:p-8 space-y-6">
              <h2 className="font-display text-xl font-bold">Verification details</h2>

              {!bt && (
                <p className="text-sm text-muted-foreground">
                  Choose a business type above to see the required verification fields.
                </p>
              )}

              {bt === "sole_proprietor" && (
                <Field label="GSTIN" required hint="Format: 15 characters, e.g. state code + PAN + entity code (letter + Z + checksum)">
                  <Input
                    value={form.tax_gst}
                    onChange={(e) => setForm((f) => ({ ...f, tax_gst: e.target.value.toUpperCase() }))}
                    className="h-12 sf-mono"
                    maxLength={15}
                    data-testid={ONBOARDING.taxGst}
                  />
                </Field>
              )}

              {bt === "partnership" && (
                <Field label="GSTIN" required hint="Format: 15 characters (state code + PAN + entity code + Z + checksum)">
                  <Input
                    value={form.tax_gst}
                    onChange={(e) => setForm((f) => ({ ...f, tax_gst: e.target.value.toUpperCase() }))}
                    className="h-12 sf-mono"
                    maxLength={15}
                    data-testid={ONBOARDING.taxGst}
                  />
                </Field>
              )}

              {(bt === "company" || bt === "limited_liability_partnership") && (
                <>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Field label={bt === "company" ? "CIN" : "LLP ID"} required>
                      <Input value={form.business_id} onChange={set("business_id")} className="h-12" data-testid={ONBOARDING.businessId} />
                    </Field>
                    <Field label="Business PAN" required>
                      <Input value={form.tax_id} onChange={set("tax_id")} className="h-12" data-testid={ONBOARDING.taxId} />
                    </Field>
                  </div>
                  <Field label="GSTIN" required hint="Format: 15 characters (state code + PAN + entity code + Z + checksum)">
                    <Input
                      value={form.tax_gst}
                      onChange={(e) => setForm((f) => ({ ...f, tax_gst: e.target.value.toUpperCase() }))}
                      className="h-12 sf-mono"
                      maxLength={15}
                      data-testid={ONBOARDING.taxGst}
                    />
                  </Field>
                </>
              )}
            </CardContent>
          </Card>

          {/* Payout bank account (INR) */}
          <Card>
            <CardContent className="p-6 md:p-8 space-y-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                  <Landmark className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-bold">Payout bank account</h2>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md">
                    {"This is where your rupees land after each USD, GBP, or EUR deposit is converted. Currency is fixed as INR."}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Account holder name" required>
                  <Input value={form.payout_holder} onChange={set("payout_holder")} placeholder="As per bank records" className="h-12" data-testid={ONBOARDING.payoutHolder} />
                </Field>
                <Field label="Currency">
                  <Input value="INR" readOnly disabled className="h-12 bg-muted" data-testid="onb-payout-currency" />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Bank account number" required hint="Numeric only, as printed on your passbook or bank statement">
                  <Input value={form.payout_number} onChange={set("payout_number")} className="h-12 sf-mono" data-testid={ONBOARDING.payoutNumber} />
                </Field>
                <Field label="IFSC code" required>
                  <Input
                    value={form.payout_ifsc}
                    onChange={(e) => setForm((f) => ({ ...f, payout_ifsc: e.target.value.toUpperCase() }))}
                    placeholder="HDFC0000261"
                    className="h-12 sf-mono"
                    maxLength={11}
                    data-testid={ONBOARDING.payoutIfsc}
                  />
                </Field>
              </div>

              <div className="flex items-start gap-3 pt-2">
                <Checkbox
                  id="payout-same-address"
                  checked={!!form.payout_same_as_business}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, payout_same_as_business: !!v }))}
                  data-testid={ONBOARDING.payoutSameAddress}
                  className="mt-1"
                />
                <label htmlFor="payout-same-address" className="text-sm text-foreground cursor-pointer">
                  Billing address is the same as my business address
                </label>
              </div>

              {!form.payout_same_as_business && (
                <div className="space-y-6 pt-2">
                  <Field label="Address line" required>
                    <Input value={form.payout_address_line} onChange={set("payout_address_line")} className="h-12" data-testid={ONBOARDING.payoutAddressLine} />
                  </Field>
                  <div className="grid md:grid-cols-3 gap-6">
                    <Field label="City" required>
                      <Input value={form.payout_city} onChange={set("payout_city")} className="h-12" data-testid={ONBOARDING.payoutCity} />
                    </Field>
                    <Field label="State" required>
                      <Input value={form.payout_state} onChange={set("payout_state")} className="h-12" data-testid={ONBOARDING.payoutState} />
                    </Field>
                    <Field label="Postal code" required>
                      <Input value={form.payout_postal_code} onChange={set("payout_postal_code")} className="h-12" data-testid={ONBOARDING.payoutPostal} />
                    </Field>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <p className="text-sm text-muted-foreground max-w-md">
              {"We'll set up your USD receiving account in the background. You can access your dashboard right away."}
            </p>
            <Button type="submit" disabled={loading} className="h-14 px-8 text-base font-semibold rounded-lg" data-testid={ONBOARDING.submitBtn}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  Finish setup <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        {label} {required && <span className="text-red-500" aria-hidden="true">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
